import React from 'react';
import { BasePage } from '../../components/base-page';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery, useMutation } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useAlert } from '@dwelvio/react-alert';
import { useAuth } from '@dwelvio/auth-react';
import { IUserData } from '../../types';
import {
  GetNotificationSettingsDocument,
  GetNotificationSettingsQuery,
  GetNotificationSettingsQueryVariables,
  UpdateNotificationSettingsDocument,
  UpdateNotificationSettingsMutation,
  UpdateNotificationSettingsMutationVariables,
  NotificationInput,
} from '../../generated/graphql';
import {
  DEFAULT_NOTIFICATION_SUBJECT,
  DEFAULT_NOTIFICATION_TEXT,
  DEFAULT_PAYMENT_CONFIRMATION_SUBJECT,
  DEFAULT_PAYMENT_CONFIRMATION_TEXT,
  DEFAULT_INVOICE_INFORMATION_SUBJECT,
  DEFAULT_INVOICE_INFORMATION_TEXT,
} from '../../lib/notification-constants';

export function NotificationSettingsPage() {
  const { show } = useAlert();
  const { user } = useAuth<IUserData>();

  const { data, loading, error, refetch } = useQuery<
    GetNotificationSettingsQuery,
    GetNotificationSettingsQueryVariables
  >(GetNotificationSettingsDocument, {
    variables: { accountId: user?.accountId ?? '' },
    skip: !user?.accountId,
  });

  const [updateSettings] = useMutation<UpdateNotificationSettingsMutation, UpdateNotificationSettingsMutationVariables>(
    UpdateNotificationSettingsDocument,
    {
      onError: (e: Error) => show.error('Nepodarilo sa uložiť nastavenia.', e.message),
    },
  );

  const [notifications, setNotifications] = React.useState<NotificationInput[]>([]);
  const [paymentConfirmationEnabled, setPaymentConfirmationEnabled] = React.useState(false);
  const [paymentConfirmationSubject, setPaymentConfirmationSubject] = React.useState(
    DEFAULT_PAYMENT_CONFIRMATION_SUBJECT,
  );
  const [paymentConfirmationText, setPaymentConfirmationText] = React.useState(DEFAULT_PAYMENT_CONFIRMATION_TEXT);
  const [invoiceInformationSubject, setInvoiceInformationSubject] = React.useState(DEFAULT_INVOICE_INFORMATION_SUBJECT);
  const [invoiceInformationText, setInvoiceInformationText] = React.useState(DEFAULT_INVOICE_INFORMATION_TEXT);
  const [notificationBccEmails, setNotificationBccEmails] = React.useState('');
  const [bccEmailError, setBccEmailError] = React.useState('');

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať nastavenia.', error.message);
    }
  }, [error, show]);

  React.useEffect(() => {
    if (data?.getNotificationSettings) {
      const settings = data.getNotificationSettings;
      setNotifications(
        settings.notifications.map((n) => ({
          id: n.id,
          daysBefore: n.daysBefore,
          subject: n.subject,
          text: n.text,
          active: n.active,
        })),
      );
      setPaymentConfirmationEnabled(settings.paymentConfirmationEnabled ?? false);
      setPaymentConfirmationSubject(settings.paymentConfirmationSubject ?? DEFAULT_PAYMENT_CONFIRMATION_SUBJECT);
      setPaymentConfirmationText(settings.paymentConfirmationText ?? DEFAULT_PAYMENT_CONFIRMATION_TEXT);
      setInvoiceInformationSubject(settings.invoiceInformationSubject ?? DEFAULT_INVOICE_INFORMATION_SUBJECT);
      setInvoiceInformationText(settings.invoiceInformationText ?? DEFAULT_INVOICE_INFORMATION_TEXT);
      setNotificationBccEmails(settings.notificationBccEmails ?? '');
    }
  }, [data]);

  const handleAdd = () => {
    setNotifications([
      ...notifications,
      {
        id: undefined,
        daysBefore: 7,
        active: true,
        subject: DEFAULT_NOTIFICATION_SUBJECT,
        text: DEFAULT_NOTIFICATION_TEXT,
      },
    ]);
  };

  const handleRemove = (index: number) => {
    setNotifications(notifications.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof NotificationInput, value: string | number | boolean) => {
    const updated = [...notifications];
    updated[index] = { ...updated[index], [field]: value };
    setNotifications(updated);
  };

  const validateBccEmails = (value: string): boolean => {
    if (!value.trim()) {
      setBccEmailError('');
      return true;
    }
    const emails = value.split(',').map((e) => e.trim());
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    for (const email of emails) {
      if (!emailRegex.test(email)) {
        setBccEmailError('Neplatná emailová adresa. Použite čiarku na oddelenie viacerých adries.');
        return false;
      }
    }
    setBccEmailError('');
    return true;
  };

  const handleBccEmailChange = (value: string) => {
    setNotificationBccEmails(value);
    validateBccEmails(value);
  };

  const handleSaveBccEmails = () => {
    if (!user?.accountId) return;

    // Validate BCC emails before saving
    if (!validateBccEmails(notificationBccEmails)) {
      show.error('Chyba pri ukladaní', 'Prosím, opravte emailové adresy.');
      return;
    }

    updateSettings({
      variables: {
        accountId: user.accountId,
        input: {
          notifications,
          paymentConfirmationEnabled,
          paymentConfirmationSubject,
          paymentConfirmationText,
          invoiceInformationSubject,
          invoiceInformationText,
          notificationBccEmails,
        },
      },
      onCompleted: () => {
        show.info('Nastavenia boli úspešne uložené.');
        refetch();
      },
    });
  };

  const handleSaveNotification = (index: number) => {
    if (!user?.accountId) return;

    updateSettings({
      variables: {
        accountId: user.accountId,
        input: {
          notifications,
          paymentConfirmationEnabled,
          paymentConfirmationSubject,
          paymentConfirmationText,
          invoiceInformationSubject,
          invoiceInformationText,
          notificationBccEmails,
        },
      },
      onCompleted: () => {
        show.info('Nastavenia pripomienok boli úspešne uložené.');
        refetch();
      },
    });
  };

  const handleSavePaymentConfirmation = () => {
    if (!user?.accountId) return;

    updateSettings({
      variables: {
        accountId: user.accountId,
        input: {
          notifications,
          paymentConfirmationEnabled,
          paymentConfirmationSubject,
          paymentConfirmationText,
          invoiceInformationSubject,
          invoiceInformationText,
          notificationBccEmails,
        },
      },
      onCompleted: () => {
        show.info('Nastavenia boli úspešne uložené.');
        refetch();
      },
    });
  };

  const handleSaveInvoiceInformation = () => {
    if (!user?.accountId) return;

    updateSettings({
      variables: {
        accountId: user.accountId,
        input: {
          notifications,
          paymentConfirmationEnabled,
          paymentConfirmationSubject,
          paymentConfirmationText,
          invoiceInformationSubject,
          invoiceInformationText,
          notificationBccEmails,
        },
      },
      onCompleted: () => {
        show.info('Nastavenia boli úspešne uložené.');
        refetch();
      },
    });
  };

  const dayOptions: number[] = [];
  for (let i = -30; i <= 30; i++) {
    dayOptions.push(i);
  }

  return (
    <BasePage
      title="Nastavenia pripomienok faktúr"
      loading={loading}
      errorMsg={data ? undefined : 'Nepodarilo sa načítať nastavenia.'}
    >
      <Box maxWidth={800}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Pripomienky platby
        </Typography>

        <Card sx={{ marginBottom: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
              Skrytá kópia (BCC) emailových notifikácií
            </Typography>
            <Typography variant="body2" component="div" sx={{ marginBottom: 2 }}>
              Zadajte emailovú adresu alebo viacero emailových adries oddelených čiarkou, na ktoré budú zasielané skryté
              kópie pripomienok o platbách a potvrdení o prijatí platby.
            </Typography>
            <TextField
              label="BCC emailové adresy"
              fullWidth
              value={notificationBccEmails}
              onChange={(e) => handleBccEmailChange(e.target.value)}
              error={!!bccEmailError}
              helperText={bccEmailError || 'Napríklad: email1@example.com, email2@example.com'}
              placeholder="email1@example.com, email2@example.com"
            />
            <Box sx={{ marginTop: 2 }}>
              <Button variant="contained" onClick={handleSaveBccEmails}>
                Uložiť BCC emailové adresy
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="body1" sx={{ marginBottom: 2 }}>
          Tu môžete nastaviť, kedy majú byť odosielané emailové pripomienky o nezaplatených faktúrach. Môžete vytvoriť
          viacero pripomienok s rôznymi textami a v rôznych časoch pred (alebo po) splatnosti faktúry.
        </Typography>

        <Typography variant="body2" component="div" sx={{ marginBottom: 2 }}>
          V texte pripomienky môžete používať nasledovné makrá:
          <ul>
            <li>
              <strong>{'  {{DUEDATE}}'}</strong> - dátum splatnosti faktúry
            </li>
            <li>
              <strong>{'  {{INVOICENO}}'}</strong> - číslo faktúry
            </li>
            <li>
              <strong>{'  {{INVOICEDESC}}'}</strong> - popis faktúry
            </li>
            <li>
              <strong>{'  {{INVOICEREF}}'}</strong> - referenčné číslo faktúry
            </li>
            <li>
              <strong>{'  {{VALUE}}'}</strong> - hodnota faktúry
            </li>
            <li>
              <strong>{'  {{CONTRACTNO}}'}</strong> - číslo zmluvy
            </li>
            <li>
              <strong>{'  {{UNIT}}'}</strong> - názov nehnuteľnosti (prvá nehnuteľnosť zo zmluvy)
            </li>
            <li>
              <strong>{'  {{QRCODE}}'}</strong> - QR kód pre platbu (vyžaduje nastavený bankový účet v profile účtu)
            </li>
            <li>
              <strong>{'  {{BANKACCOUNT}}'}</strong> - bankový účet prenajímateľa
            </li>
            <li>
              <strong>{'  {{BANKACCOUNTNAME}}'}</strong> - názov bankového účtu prenajímateľa
            </li>
          </ul>
        </Typography>

        <Stack spacing={2} sx={{ marginTop: 3 }}>
          {notifications.map((notification, index) => (
            <Card key={index} sx={{ opacity: notification.active ? 1 : 0.6 }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={notification.active}
                          onChange={(e) => handleChange(index, 'active', e.target.checked)}
                        />
                      }
                      label="Aktívna"
                    />
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel id={`days-before-label-${index}`}>Počet dní pred splatnosťou</InputLabel>
                      <Select
                        labelId={`days-before-label-${index}`}
                        value={notification.daysBefore}
                        label="Počet dní pred splatnosťou"
                        onChange={(e) => handleChange(index, 'daysBefore', Number(e.target.value))}
                        disabled={!notification.active}
                      >
                        {dayOptions.map((day) => (
                          <MenuItem key={day} value={day}>
                            {day > 0 ? `${day} dní pred` : day < 0 ? `${Math.abs(day)} dní po` : 'V deň splatnosti'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton onClick={() => handleRemove(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <TextField
                    label="Predmet emailu"
                    fullWidth
                    value={notification.subject}
                    onChange={(e) => handleChange(index, 'subject', e.target.value)}
                    disabled={!notification.active}
                    helperText="Môžete použiť makrá ako {{INVOICENO}}, {{DUEDATE}}, atď."
                  />

                  <TextField
                    label="Text pripomienky"
                    multiline
                    rows={8}
                    fullWidth
                    value={notification.text}
                    onChange={(e) => handleChange(index, 'text', e.target.value)}
                    disabled={!notification.active}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={() => handleSaveNotification(index)}>
                      Uložiť pripomienku
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          ))}

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
              Pridať pripomienku
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ marginY: 4 }} />

        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Nastavenia fakturácie
        </Typography>

        <Stack spacing={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentConfirmationEnabled}
                onChange={(e) => setPaymentConfirmationEnabled(e.target.checked)}
              />
            }
            label="Povoliť automatické zasielanie potvrdení o prijatí platby"
          />

          {paymentConfirmationEnabled && (
            <>
              <TextField
                label="Predmet emailu"
                fullWidth
                value={paymentConfirmationSubject}
                onChange={(e) => setPaymentConfirmationSubject(e.target.value)}
                helperText="Môžete použiť makrá ako {{INVOICENO}}, {{VALUE}}, atď."
              />

              <TextField
                label="Text potvrdenia"
                multiline
                rows={10}
                fullWidth
                value={paymentConfirmationText}
                onChange={(e) => setPaymentConfirmationText(e.target.value)}
                helperText="Môžete použiť rovnaké makrá ako pri pripomienkach"
              />
            </>
          )}

          <Button variant="contained" onClick={handleSavePaymentConfirmation}>
            Uložiť nastavenia potvrdenia o platbe
          </Button>

          <Divider sx={{ marginY: 2 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Informačný email pri odoslaní faktúry
          </Typography>

          <TextField
            label="Predmet emailu"
            fullWidth
            value={invoiceInformationSubject}
            onChange={(e) => setInvoiceInformationSubject(e.target.value)}
            helperText="Môžete použiť makrá ako {{INVOICENO}}, {{DUEDATE}}, {{VALUE}}, atď."
          />

          <TextField
            label="Text informačného emailu"
            multiline
            rows={10}
            fullWidth
            value={invoiceInformationText}
            onChange={(e) => setInvoiceInformationText(e.target.value)}
            helperText="Môžete použiť rovnaké makrá ako pri pripomienkach"
          />

          <Button variant="contained" onClick={handleSaveInvoiceInformation}>
            Uložiť nastavenia informačného emailu
          </Button>
        </Stack>
      </Box>
    </BasePage>
  );
}
